/**
 * Logto Callback Page
 *
 * This page handles the OAuth callback from Logto after user authentication.
 * It processes the callback, syncs the user with our backend, and redirects.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHandleSignInCallback } from '@logto/react';
import { useAuth } from '../../auth/useAuth';
import { useAuthStore } from '../../stores/authStore';
import authService from '../../services/authService';
import { User, UserRole, UserRoles } from '../../types';
import { useTranslation } from 'react-i18next';
import { consumeReturnTo } from '../../lib/authReturnTo';

const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getIdToken, signOut } = useAuth();
  const { actions } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [isAccountDeleted, setIsAccountDeleted] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [callbackTimeout, setCallbackTimeout] = useState(false);
  const { t } = useTranslation();

  // Handle the sign-in callback from Logto
  const { isLoading, error: callbackError } = useHandleSignInCallback(() => {
    // This callback is called after successful authentication
    // We'll sync the user in the useEffect below
  });

  // Timeout if callback takes too long (10 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading && !isAuthenticated) {
        setCallbackTimeout(true);
        setError(t('auth.callbackTimeout'));
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isLoading, isAuthenticated, t]);

  // Handle callback errors
  useEffect(() => {
    if (callbackError) {
      console.error('Logto callback error:', callbackError);
      setError(t('auth.callbackFailed'));
      // Mark sync as failed to prevent infinite loops in ProtectedRoute
      sessionStorage.setItem('auth_sync_failed', 'true');
    }
  }, [callbackError, t]);

  useEffect(() => {
    const syncUserWithBackend = async () => {
      // Prevent multiple syncs and wait for Logto to be ready
      // Also check for timeout or errors
      if (isLoading || !isAuthenticated || isSyncing || callbackTimeout || error) return;

      setIsSyncing(true);

      try {
        // Use ID Token for backend synchronization as it is a JWT.
        // The access token without a configured resource is opaque and causes "Not enough segments" error in the backend.
        const token = await getIdToken();

        if (!token) {
          throw new Error(t('auth.idTokenFailed'));
        }

        // IMPORTANT: Mark login time to prevent 401 race conditions from clearing auth
        authService.markLoginTime();

        // IMPORTANT: Store token in authStore BEFORE making API calls
        // This ensures subsequent requests have the token available
        actions.setTokens({ access_token: token });

        // Log le token pour debug (à retirer en prod)
        // eslint-disable-next-line no-console
        console.log('JWT ID Token utilisé pour le backend:', token.substring(0, 20) + '...');

        // Sync user with notre backend
        const backendUser = await authService.syncUserWithBackend(token);

        // Update our auth store with the synced user
        const user: User = {
          id: backendUser.id,
          email: backendUser.email,
          name: backendUser.name || `${backendUser.first_name || ''} ${backendUser.last_name || ''}`.trim() || backendUser.email,
          role: (backendUser.role as UserRole) || UserRoles.User,
          is_active: backendUser.is_active,
          organization_id: backendUser.organization_id,
          organization_is_active: backendUser.organization_is_active,
          avatar_url: backendUser.avatar_url,
          preferences: backendUser.preferences,
          logto_id: backendUser.logto_id,
          // Carry the profile so route guards decide on it, not on the role fallback.
          profile_id: (backendUser as Pick<User, 'profile_id'>).profile_id ?? null,
          profile: (backendUser as Pick<User, 'profile'>).profile ?? null,
        };

        actions.loginSuccess(
          {
            access_token: token,
            refresh_token: '' // Logto handles refresh internally
          },
          user
        );

        // Success! Clear failure flag
        sessionStorage.removeItem('auth_sync_failed');

        // Small delay to ensure store is persisted before navigation
        await new Promise(resolve => setTimeout(resolve, 500));

        // Back to the page the user asked for before signing in, else the
        // index route, which resolves the profile's landing page.
        navigate(consumeReturnTo() ?? '/', { replace: true });

      } catch (err) {
        console.error('Error syncing user with backend:', err);
        // Detect account_deleted 403 from the backend
        if (authService.isAccountDeletedError(err)) {
          setIsAccountDeleted(true);
          setIsSyncing(false);
          return;
        }
        setError(t('auth.syncFailed.message'));
        setIsSyncing(false);
        // Mark sync as failed to prevent infinite loops in ProtectedRoute
        sessionStorage.setItem('auth_sync_failed', 'true');
      }
    };

    syncUserWithBackend();
  }, [isLoading, isAuthenticated, getIdToken, actions, navigate, isSyncing, callbackTimeout, error, t]);

  if (isAccountDeleted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-8 max-w-md">
          <div className="text-5xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.accountDeleted.title')}</h1>
          <p className="text-gray-600 mb-6">{t('auth.accountDeleted.message')}</p>
          <button
            onClick={() => {
              sessionStorage.removeItem('auth_sync_failed');
              signOut(window.location.origin);
            }}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700"
          >
            {t('auth.accountDeleted.signOut')}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-8 max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.error.title')}</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              {t('auth.error.returnToHome')}
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem('auth_sync_failed');
                signOut(window.location.origin);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              {t('auth.error.signOutAndRetry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">{t('auth.authenticating')}</h2>
        <p className="text-gray-500 mt-2">{t('auth.pleaseWait')}</p>
      </div>
    </div>
  );
};

export default CallbackPage;
